import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { Alert } from '@material-ui/lab';
import withTheme from '@material-ui/core/styles/withTheme';
import moment from 'moment';
import BookingSummaryContainer from '../../containers/BookingSummaryContainer';
import {
    checkBookerFieldsAreOk,
    checkCustomBookingFieldsAreOk,
    checkGuestFieldsAreOk,
    getBookerFieldsFromCartitems
} from '../../guestfields';
import BookingProgressBar from '../../components/fancy/BookingProgressBar';
import CartItem from './components/CartItem';
import CartPageCalendar from '../../components/atom/CartPageCalendar';
import FancySelectButton from '../../components/fancy/FancySelectButton';
import { EditIcon, TimesIcon } from '../../icons';
import { useGA } from '../../hooks/useGoogleAnalytics';
import GuestActions from '../review/components/GuestActions';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';

const CartPage = ({ cartItems, customer, booker, user, removeCartItem, createGuest, theme }) => {
    const [cartError, setCartError] = useState(false);
    const [isCalendarView, setIsCalendarView] = useState(false);
    const isAgent = user && user.role === 'agent';
    const isAdmin = user && user.role === 'admin';
    const customerId = customer?.id || user?.id;
    const { track } = useGA();

    // Fetch bookings once for all cart items to check for overlaps
    const isGuestUser = user?.id === customer?.id;
    const userId = isGuestUser ? 'me' : customer?.id;

    // Calculate date range covering all cart items
    let earliestStart = null;
    let latestEnd = null;
    if (cartItems && cartItems.length > 0) {
        cartItems.forEach(item => {
            if (item?.start_time) {
                if (!earliestStart || moment(item.start_time).isBefore(earliestStart)) {
                    earliestStart = moment(item.start_time);
                }
            }
            if (item?.end_time) {
                if (!latestEnd || moment(item.end_time).isAfter(latestEnd)) {
                    latestEnd = moment(item.end_time);
                }
            }
        });
    }

    const startOfDay = earliestStart ? earliestStart.startOf('day').toLocaleString() : '';
    const endOfDay = latestEnd ? latestEnd.endOf('day').toLocaleString() : '';

    const [bookingsData] = useSwaggerClient(
        'users',
        'getBookingsByUserId',
        {
            userId,
            future: true,
            page: 0,
            pageSize: 100,
            startDate: startOfDay,
            endDate: endOfDay,
            filters: JSON.stringify({ status: 'all' }),
            calendar: true
        },
        [],
        data =>
            data.bookingItems.filter(booking =>
                ['confirmed', 'unconfirmed'].includes(booking.status)
            ),
        // Skip fetching if no date range or no user
        !startOfDay || !endOfDay || !userId
    );

    const handleDeleteCartItem = index => {
        const cartItem = cartItems[index];
        const items = cartItem.tickets.map(ticket => ({
            item_id: `${cartItem?.activity?.id || ''}-${ticket.id}`,
            item_name: `${cartItem?.activity?.name || ''} - ${ticket.name}`,
            price: ticket.ticket_retail_price || ticket.retail_price,
            quantity: ticket.quantity || 1
        }));
        track.removeFromCart({
            items,
            value: items?.reduce((total, item) => total + item.price * item.quantity, 0)
        });
        removeCartItem(index);
    };

    // booker fields needs to be collected for ALL tickets (even if they are not connected to the cartitem)
    const bookerFields = getBookerFieldsFromCartitems(cartItems);
    const bookerRequiredFieldsOk = checkBookerFieldsAreOk(booker, bookerFields, isAgent);

    // checking every cartitem, within that every ticket, with every ticket every guest
    const guestsRequiredFieldsOk = checkGuestFieldsAreOk(cartItems, isAgent);

    // check if agent filled out every custom booking field
    const customBookingRequiredFieldsOk = checkCustomBookingFieldsAreOk(cartItems);

    const agentOrGuestCanGoToPayment =
        // user is not logged in -> will have fast reg
        // user is agent or admin -> need to have a guest selected
        // user is customer -> can go to payment
        user === null || ((isAgent || isAdmin) && customer) || (user && !isAgent && !isAdmin);

    return (
        <Grid container className="page" spacing={2}>
            <Grid item container md={8} xs={12} direction="column" justifyContent="flex-start">
                <Link to="/" className="link mb-2" style={{ color: theme.palette.primary.main }}>
                    Add another item
                </Link>
                <h1 className="pageheader">Cart</h1>
                <FancySelectButton
                    style={{ display: 'none' }}
                    size="small"
                    highlight={isCalendarView}
                    onClick={() => setIsCalendarView(!isCalendarView)}
                >
                    {!isCalendarView ? 'Show calendar' : 'Hide calendar'}
                </FancySelectButton>
            </Grid>
            <Grid item className="mb-2" md={4} xs={12}>
                <BookingProgressBar activeStep={1} />
            </Grid>

            {!cartItems || !cartItems.length ? (
                <Grid
                    item
                    container
                    className="mb-3 empty-cart-section"
                    xs={12}
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="center"
                >
                    <span className="title">Your Cart is Empty</span>
                </Grid>
            ) : (
                <React.Fragment>
                    {isCalendarView && (
                        <Grid item container xs={12} md={8} style={{ alignItems: 'flex-start' }}>
                            <CartPageCalendar
                                label="Bookings calendar"
                                guestId={customerId}
                                cart={cartItems}
                            />
                        </Grid>
                    )}
                    {!isCalendarView && (
                        <Grid item container xs={12} md={8} direction="column">
                            {cartItems.map((item, index) => {
                                const details = cartError?.response?.obj?.details ?? {};
                                const outdateError = (details.outOfDate ?? []).filter(
                                    e => e.idx === index
                                );
                                const overbookError = (details.overBook ?? []).filter(
                                    e => e.idx === index
                                );
                                return (
                                    <CartItem
                                        user={user}
                                        customer={customer}
                                        key={index}
                                        cartItem={item}
                                        isAgent={isAgent}
                                        booker={booker}
                                        bookingsData={bookingsData}
                                        hasErrors={
                                            outdateError?.length > 0 || overbookError?.length > 0
                                        }
                                    >
                                        {[...outdateError, ...overbookError].length > 0 && (
                                            <Grid item key={index}>
                                                <Alert severity="warning" key={`${index}-alert`}>
                                                    {[...outdateError, ...overbookError].map(
                                                        (e, i) => (
                                                            <div key={`${index}-${i}`}>
                                                                {e.message}
                                                            </div>
                                                        )
                                                    )}
                                                </Alert>
                                            </Grid>
                                        )}
                                        <Grid item>
                                            <Link to={`/review/${index}`}>
                                                <Button color="primary" startIcon={<EditIcon />}>
                                                    Modify
                                                </Button>
                                            </Link>
                                            <Button
                                                onClick={() => handleDeleteCartItem(index)}
                                                startIcon={<TimesIcon />}
                                            >
                                                Delete
                                            </Button>
                                        </Grid>
                                    </CartItem>
                                );
                            })}
                        </Grid>
                    )}
                    <Grid item container xs={12} md={4} direction="column">
                        <BookingSummaryContainer
                            page="cart"
                            cartItems={cartItems}
                            className="mb-2"
                            setCartError={setCartError}
                        >
                            {agentOrGuestCanGoToPayment && (
                                <Grid item xs={12} className="text-right">
                                    {bookerRequiredFieldsOk &&
                                    guestsRequiredFieldsOk &&
                                    customBookingRequiredFieldsOk ? (
                                        <Link to="/payment">
                                            <Button
                                                fullWidth
                                                className="btn-squared"
                                                variant="contained"
                                                color="primary"
                                            >
                                                Proceed to Payment
                                            </Button>
                                        </Link>
                                    ) : (
                                        <Button
                                            fullWidth
                                            className="btn-squared"
                                            variant="contained"
                                            color="primary"
                                            disabled
                                        >
                                            Some items needs your attention
                                        </Button>
                                    )}
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                {(isAgent || isAdmin) &&
                                    !customer &&
                                    bookerRequiredFieldsOk &&
                                    guestsRequiredFieldsOk &&
                                    customBookingRequiredFieldsOk && (
                                        <GuestActions createGuest={createGuest} />
                                    )}
                            </Grid>
                        </BookingSummaryContainer>
                    </Grid>
                </React.Fragment>
            )}
        </Grid>
    );
};

export default withTheme(CartPage);
